import ButtonArrow from '@img/icons/icon-button-arrow.svg';
import ButtonDownloadIcon from '@img/icons/icon-download.svg';
import Lock from '@img/icons/icon-lock.svg';
import MinusIcon from '@img/icons/icon-minus.svg';
import ButtonX from '@img/icons/icon-x.svg';
import Location from '@img/icons/location.svg';
import PlusIcon from '@img/icons/plus.svg';
import UploadIcon from '@img/icons/upload.svg';
import ViewInAr from '@img/icons/view-in-ar.svg';
import OneKey from '@img/logos/brand/tech-onekey.svg';
import GalleryIcon from '@stories/Elements/Media/GalleryIcon/GalleryIcon';
import Loader from '@stories/Elements/Misc/Loader/Loader.hybrid';
import React, { forwardRef, ReactNode } from 'react';
import { ButtonIcon, ButtonStyles } from './Button.styles';

export type ButtonIconT =
  | 'arrow'
  | 'download'
  | 'gallery'
  | 'location'
  | 'minus'
  | 'none'
  | 'plus'
  | 'upload'
  | 'view-in-ar'
  | 'loader'
  | 'x'
  | 'onekey'
  | 'lock';

export type ButtonTheme =
  | 'red'
  | 'red-white'
  | 'white'
  | 'black'
  | 'black-grey'
  | 'grey'
  | 'grey-outline'
  | 'dark-grey'
  | 'shimmer'
  | 'green'
  | 'black-outline'
  | 'white-red';

export interface ButtonProps {
  id?: string;
  alignCentre?: boolean;
  text?: string;
  theme?: ButtonTheme;
  size?: 'small' | 'normal';
  icon?: ButtonIconT;
  iconPlacement?: 'before' | 'after';
  textTheme?: 'black';
  underline?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  progress?: boolean;
  fakeButton?: boolean;
  attributes?: Record<string, string>;
  style?: React.CSSProperties;
  iconVisibility?: IconVisibilityT;
  maintainWidth?: boolean;
  children?: ReactNode;
}

export type IconVisibilityT = 'never' | 'onHover' | 'always';

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      alignCentre,
      text,
      onClick,
      textTheme,
      attributes,
      style,
      theme = 'red',
      size = 'normal',
      disabled = false,
      progress = false,
      fakeButton = false,
      underline = false,
      icon = 'none',
      iconPlacement = 'after',
      iconVisibility,
      maintainWidth = false,
      ...props
    },
    ref,
  ) => {
    function shouldRenderIcon(): IconVisibilityT {
      if (iconVisibility) {
        return iconVisibility;
      }
      if (icon === 'download' || icon === 'upload' || icon === 'lock' || progress) {
        return 'always';
      }
      if (icon === null || icon === undefined || icon === 'none') {
        return 'never';
      }

      return 'onHover';
    }

    const _iconVisibility: IconVisibilityT = shouldRenderIcon();
    const _icon = progress ? 'loader' : icon;

    const _onClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
      if (fakeButton) {
        return;
      }

      if (disabled || progress || !onClick) {
        event.preventDefault();
        return;
      }

      onClick(event);
    };

    return (
      <ButtonStyles.ButtonElement
        ref={ref}
        id={props.id}
        alignCentre={alignCentre}
        disabled={disabled || progress || _icon === 'loader'}
        data-progress={progress}
        iconPadding={_icon === 'arrow'}
        theme={theme}
        textTheme={textTheme}
        size={size}
        underline={underline}
        onClick={_onClick}
        data-icon={_icon}
        data-icon-placement={iconPlacement}
        data-icon-visibility={_iconVisibility}
        maintainWidth={maintainWidth}
        style={style}
        data-track={true}
        data-text={text}
        {...attributes}
      >
        {props.children ? (
          <>{props.children}</>
        ) : (
          <>
            {iconPlacement === 'before' && renderIcon(_icon)}
            {icon !== 'onekey' && text}
            {iconPlacement === 'after' && renderIcon(_icon)}
          </>
        )}
      </ButtonStyles.ButtonElement>
    );
  },
);

Button.displayName = 'Button';

function renderIcon(icon: ButtonIconT, textTheme?: 'black'): ReactNode {
  switch (icon) {
    case 'none': {
      return null;
    }

    case 'loader': {
      return (
        <ButtonIcon>
          <Loader theme={textTheme || 'white'} />
        </ButtonIcon>
      );
    }

    case 'download': {
      return (
        <ButtonIcon>
          <ButtonDownloadIcon />
        </ButtonIcon>
      );
    }

    default:
    case 'arrow': {
      return (
        <ButtonIcon>
          <ButtonArrow />
        </ButtonIcon>
      );
    }

    case 'upload':
      return (
        <ButtonIcon>
          <UploadIcon />
        </ButtonIcon>
      );

    case 'location':
      return <Location />;

    case 'x': {
      return (
        <ButtonIcon>
          <ButtonX />
        </ButtonIcon>
      );
    }

    case 'view-in-ar': {
      return <ViewInAr />;
    }

    case 'gallery': {
      return (
        <ButtonIcon>
          <GalleryIcon />
        </ButtonIcon>
      );
    }

    case 'plus': {
      return (
        <ButtonIcon>
          <PlusIcon />
        </ButtonIcon>
      );
    }

    case 'minus': {
      return (
        <ButtonIcon>
          <MinusIcon />
        </ButtonIcon>
      );
    }

    case 'onekey': {
      return (
        <ButtonIcon>
          <OneKey />
        </ButtonIcon>
      );
    }

    case 'lock': {
      return (
        <ButtonIcon>
          <Lock />
        </ButtonIcon>
      );
    }
  }
}

export default Button;
